/**
 * TelephonyProvider  (ADDITIVE — INBOUND CALLING, Phase IV)
 *
 * The single owner of the CRM's one Twilio Device.
 *
 * WHAT IT REPLACES: nothing. Every existing consumer still calls
 * `useTwilioVoiceDevice()` and still gets `UseTwilioVoiceDeviceResult`. The only
 * difference is that inside this provider they all receive the SAME instance
 * instead of each spawning a rival Device. See the wrapper at the bottom of
 * `useTwilioVoiceDevice.ts` for the mechanism.
 *
 * WHAT IT ADDS: incoming-call handling, gated three ways —
 *   1. the server's `incomingAllow` grant (a client cannot opt itself in),
 *   2. leadership (only one tab may ring), and
 *   3. busy (a user already on a call is not offered a second one).
 *
 * The raw Twilio Device and Call objects live here and ONLY here. Neither is
 * placed on the context. Followers coordinate over BroadcastChannel with small
 * JSON messages; a Call object is never serialized or shared.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  useTwilioVoiceDeviceEngine,
  type TwilioCall,
  type TwilioDeviceInstance,
  type UseTwilioVoiceDeviceResult,
} from '../hooks/useTwilioVoiceDevice';
import { useTelephonyAvailability } from '../hooks/useTelephonyAvailability';
import { useAuth } from '../hooks/useAuth';
import { useAppDispatch } from '../store/store';
import { contactsApi } from '../api/contactsApi';
import {
  telephonyApi,
} from '../api/telephonyApi';
import {
  TelephonyContext,
  type IncomingCallInfo,
  type IncomingCallState,
  type TelephonyContextValue,
} from './telephonyContext';
import {
  buildScope,
  startLeaderElection,
  type LeaderElection,
  type SuppressionMessage,
} from './leaderElection';
import { realtimeClient } from '../realtime/socketClient';
import { RealtimeEventType } from '../realtime/events';

/**
 * Pull safe display data off the inbound Call.
 *
 * Twilio populates `customParameters` from the parameters our own inbound TwiML
 * put on the `<Client>` noun, and `parameters.From` / `.To` / `.CallSid` from the
 * call itself. Everything here therefore originates server-side. We still treat
 * it defensively: strings only, no eval, no HTML.
 */
function readIncomingInfo(call: TwilioCall, workspaceId: string): IncomingCallInfo {
  const params = (call as unknown as { parameters?: Record<string, string> }).parameters ?? {};

  const custom = (call as unknown as {
    customParameters?: Map<string, string> | Record<string, string>;
  }).customParameters;

  const readCustom = (key: string): string | undefined => {
    if (!custom) return undefined;
    const value =
      custom instanceof Map
        ? custom.get(key)
        : (custom as Record<string, string>)[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  };

  return {
    providerCallSid: typeof params.CallSid === 'string' ? params.CallSid : undefined,
    fromPhone: typeof params.From === 'string' ? params.From : 'Unknown',
    toPhone: typeof params.To === 'string' ? params.To : '',
    callerName: readCustom('CallerName'),
    contactId: readCustom('ContactId'),
    crmContactId: readCustom('CrmContactId'),
    workspaceId,
    receivedAt: Date.now(),
    recording: readCustom('Recording') === 'true',
  };
}

export interface TelephonyProviderProps {
  children: ReactNode;
}

export function TelephonyProvider({ children }: TelephonyProviderProps) {
  const { user } = useAuth();
  const { workspaceId } = useTelephonyAvailability();
  const userId = user?.id ?? null;
  const dispatch = useAppDispatch();

  const [incoming, setIncoming] = useState<IncomingCallState>({ kind: 'idle' });
  const [isLeader, setIsLeader] = useState(false);

  /**
   * ADDITIVE (incoming per-call recording toggle). Per-Call-SID
   * preference map. NOT persisted anywhere — lives only for the
   * ringing window and is cleared when the invite terminates.
   * `timestamp` is the source-device event timestamp; used for
   * last-writer-wins when web + mobile toggle at nearly the same
   * instant. Bounded implicitly by call SID reuse; also actively
   * pruned on every invite terminate (see the cleanup effect below).
   */
  const [recordingPrefs, setRecordingPrefs] = useState<
    Record<string, { record: boolean; timestamp: number; sourceDevice: 'web' | 'mobile' }>
  >({});
  const recordingPrefsRef = useRef(recordingPrefs);
  recordingPrefsRef.current = recordingPrefs;

  /** The ringing Call. Never leaves this module. */
  const incomingCallRef = useRef<TwilioCall | null>(null);
  const deviceRef = useRef<TwilioDeviceInstance | null>(null);
  const electionRef = useRef<LeaderElection | null>(null);
  const isLeaderRef = useRef(false);
  isLeaderRef.current = isLeader;

  /**
   * The engine. Not suspended — this component IS the owner. `onDeviceChange`
   * is how we get at the Device without it ever touching the context.
   */
  const handleDeviceChange = useCallback((device: TwilioDeviceInstance | null) => {
    deviceRef.current = device;
  }, []);

  const engine = useTwilioVoiceDeviceEngine({ onDeviceChange: handleDeviceChange });

  const { status, activeCall, attachIncomingCall } = engine;

  /**
   * The PUBLIC voice surface — the engine minus its provider-only internals.
   *
   * `attachIncomingCall` takes a raw Twilio Call, so leaving it on the context
   * would hand every consumer a way to reach into the SDK. Strip it here; the
   * provider closed over it above and is the only thing that can call it.
   */
  const voice = useMemo<UseTwilioVoiceDeviceResult>(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attachIncomingCall: _internal, ...publicSurface } = engine;
    return publicSurface;
  }, [engine]);

  /** Server-authoritative. The browser never grants itself incoming. */
  const incomingAllowed = status.kind === 'ready' && status.incomingAllow === true;

  /** One call at a time. An active call of EITHER direction makes us busy. */
  const isBusy = activeCall.kind === 'connecting' || activeCall.kind === 'in_call';
  const isBusyRef = useRef(isBusy);
  isBusyRef.current = isBusy;

  /**
   * Release a still-ringing held call with a REAL reject and clear the modal.
   * `incomingCallRef` is only ever set while a call is RINGING/ACCEPTING (it is
   * nulled the instant accept hands the call to the engine), so this never drops
   * an established in_call. Used by the leadership-loss guards (#3/#4).
   */
  const releaseRingingIfHeld = useCallback(() => {
    const held = incomingCallRef.current;
    if (held) {
      incomingCallRef.current = null;
      try {
        held.reject?.();
      } catch {
        /* ignore */
      }
    }
    setIncoming((prev) => (prev.kind === 'idle' ? prev : { kind: 'idle' }));
  }, []);

  // ----------------------------------------------------------------
  // Leader election — scoped to (crm, workspace, user)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!workspaceId || !userId) {
      setIsLeader(false);
      return;
    }

    const scope = buildScope(workspaceId, userId);

    const election = startLeaderElection(scope, {
      onLeadershipChange: (leader) => setIsLeader(leader),
      onRemoteSuppression: (msg: SuppressionMessage) => {
        // #3 — dual-leader window: if ANOTHER tab reports it detected an incoming
        // call and we are NOT the confirmed leader, suppress our own ringing
        // immediately (real reject on any held call) rather than waiting a full
        // heartbeat for reconciliation. If we ARE the leader our claim out-ranks
        // theirs and we keep the call.
        if (msg.type === 'incoming_detected') {
          if (!isLeaderRef.current) releaseRingingIfHeld();
          return;
        }
        // Another tab handled (or saw the end of) the call. Whatever WE are
        // showing for it is now stale — close it.
        if (
          msg.type === 'incoming_accepted' ||
          msg.type === 'incoming_rejected' ||
          msg.type === 'incoming_canceled'
        ) {
          setIncoming({ kind: 'idle' });
        }
      },
    });

    electionRef.current = election;

    return () => {
      // Workspace switch, logout, or unmount: release the lease so another tab
      // can take it immediately, and drop every timer/listener.
      election.destroy();
      electionRef.current = null;
      setIsLeader(false);
      setIncoming({ kind: 'idle' });
    };
  }, [workspaceId, userId, releaseRingingIfHeld]);

  // #4 — reject on leadership loss. The moment this tab stops being the leader,
  // any still-RINGING inbound call it holds is released with a REAL reject (so
  // the server's <Dial action> routes it onward) and the modal clears. An
  // established in_call lives in the engine, not incomingCallRef, so it is never
  // touched. A non-leader can no longer hold or answer a ringing call.
  useEffect(() => {
    if (isLeader) return;
    releaseRingingIfHeld();
  }, [isLeader, releaseRingingIfHeld]);

  // ----------------------------------------------------------------
  // Incoming registration — leader + server grant only
  // ----------------------------------------------------------------
  useEffect(() => {
    const device = deviceRef.current;

    // Leader-election gate removed by request: every tab that has a
    // registered Device with an incoming-allowed grant attaches the
    // 'incoming' listener. Two independent gates remain — the Device must
    // exist, and the server-authoritative `incomingAllow` in the token
    // must be true. Twilio delivers the CallInvite to every registered
    // client under the identity; the first one to `call.accept()` wins
    // and the others receive a `cancel` event which the existing handler
    // already cleans up idempotently.
    if (!device || !incomingAllowed) return;

    const onIncoming = (...args: unknown[]) => {
      const call = args[0] as TwilioCall | undefined;
      if (!call) return;

      // BUSY: already on a call. Reject with the real SDK method so the
      // server's <Dial action> sees no-answer/busy and routes to voicemail.
      // Do NOT disturb the call in progress.
      if (isBusyRef.current || incomingCallRef.current) {
        try {
          call.reject?.();
        } catch {
          /* ignore */
        }
        return;
      }

      // Lost leadership between the effect running and the event arriving.
      if (!isLeaderRef.current) {
        try {
          call.reject?.();
        } catch {
          /* ignore */
        }
        return;
      }

      const info = readIncomingInfo(call, workspaceId ?? '');
      incomingCallRef.current = call;

      // The caller gave up before we answered.
      call.on('cancel', () => {
        incomingCallRef.current = null;
        setIncoming((prev) =>
          prev.kind === 'incoming_ringing' || prev.kind === 'accepting'
            ? { kind: 'canceled', info: prev.info }
            : prev
        );
        electionRef.current?.broadcast({
          type: 'incoming_canceled',
          callSid: info.providerCallSid,
        });
      });

      // Twilio-side failure while ringing.
      call.on('error', (...errArgs: unknown[]) => {
        const err = errArgs[0] as { message?: string } | undefined;
        incomingCallRef.current = null;
        setIncoming({
          kind: 'failed',
          info,
          // Generic and safe — never a raw Twilio payload, never credentials.
          message: err?.message
            ? 'The incoming call failed.'
            : 'The incoming call failed.',
        });
      });

      setIncoming({ kind: 'incoming_ringing', info });
      electionRef.current?.broadcast({
        type: 'incoming_detected',
        callSid: info.providerCallSid,
      });
    };

    device.on('incoming', onIncoming);

    return () => {
      // Remove EXACTLY this listener — never removeAllListeners(), which would
      // also rip out the engine's registered/error/tokenWillExpire handlers.
      try {
        (device as unknown as {
          removeListener?: (event: string, fn: (...a: unknown[]) => void) => void;
        }).removeListener?.('incoming', onIncoming);
      } catch {
        /* ignore */
      }
    };
    // `status.kind` is in the deps so we re-attach after the Device is rebuilt
    // (workspace switch / refresh), because deviceRef is a ref and does not
    // itself trigger a re-render.
  }, [incomingAllowed, isLeader, workspaceId, status.kind]);

  // ----------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------
  const acceptIncoming = useCallback(async () => {
    const call = incomingCallRef.current;
    if (!call) return;

    // Leader gate removed: every tab that received the incoming event is now
    // allowed to accept. The first `call.accept()` wins on Twilio's side —
    // Twilio delivers a `cancel` event to the losing clients and the existing
    // 'cancel' handler transitions them safely to `canceled` / `idle`.

    const info =
      incoming.kind === 'incoming_ringing' || incoming.kind === 'accepting'
        ? incoming.info
        : null;

    // #5 — never accept a call we cannot attach to the state machine. Without
    // `info` the call would connect with no UI, timer or hang-up control (the
    // orphaned-accept edge). Reject it instead.
    if (!info) {
      incomingCallRef.current = null;
      try {
        call.reject?.();
      } catch {
        /* ignore */
      }
      setIncoming({ kind: 'idle' });
      return;
    }

    setIncoming({ kind: 'accepting', info });

    try {
      call.accept?.();
    } catch {
      incomingCallRef.current = null;
      setIncoming({ kind: 'failed', info, message: 'Could not answer the call.' });
      return;
    }

    // Resolve the CRM contact for this caller. Outbound may attach a
    // `CrmContactId` custom param when it knows the caller; when it doesn't
    // (which is the common case today), fall back to a client-side phone
    // lookup against CRM's own contacts so the "View Details" button on the
    // ActiveCallShell still resolves to a real record.
    let resolvedContactId: string | undefined =
      info.crmContactId ?? info.contactId;

    if (!resolvedContactId && info.fromPhone && info.fromPhone !== 'Unknown' && workspaceId) {
      try {
        const result = await dispatch(
          contactsApi.endpoints.getContacts.initiate({
            workspaceId,
            q: info.fromPhone,
            limit: 1,
          }),
        ).unwrap();
        resolvedContactId = result?.contacts?.[0]?.id;
      } catch {
        // Lookup failure is non-fatal — the call still connects; the button
        // just stays hidden. No user-visible error.
      }
    }

    // Hand the live Call to the SAME state machine outgoing calls use, so
    // mute / hang-up / DTMF / timer / audio selection all work through the
    // existing controls and the existing ActiveCallShell renders it.
    // Adopt into the shared machine. `contactId` carries the CRM contact id
    // (same as an outbound call's activeCall.contactId), so the active-call UI
    // can open the Contact Workspace for the caller on answer.
    attachIncomingCall(call, {
      fromPhone: info.fromPhone,
      contactId: resolvedContactId,
      contactName: info.callerName,
    });

    incomingCallRef.current = null;
    setIncoming({ kind: 'idle' });

    electionRef.current?.broadcast({
      type: 'incoming_accepted',
      callSid: info.providerCallSid,
    });

    // ADDITIVE (incoming per-call recording). Only the accepting
    // device fires the recording-start request. If the losing device
    // races and reaches this point too, its `call.accept()` above
    // will have failed and we'd have returned early. Even if two
    // devices manage to POST here, the server dedupes by CallSid via
    // the CallSession.recordingSid idempotency check.
    const callSid = info.providerCallSid;
    const pref = callSid ? recordingPrefsRef.current[callSid] : undefined;
    if (callSid && pref && pref.record === true) {
      try {
        await dispatch(
          telephonyApi.endpoints.startIncomingCallRecording.initiate({
            callSid,
            record: true,
          }),
        ).unwrap();
      } catch (err) {
        // Failure NEVER ends the call. Structured log only. The user
        // will see no recording badge (which is the honest state).
        // eslint-disable-next-line no-console
        console.warn('[incomingRecording] start failed', {
          callSid,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }, [incoming, attachIncomingCall, dispatch, workspaceId]);

  const rejectIncoming = useCallback(() => {
    const call = incomingCallRef.current;

    const info =
      incoming.kind === 'incoming_ringing' || incoming.kind === 'accepting'
        ? incoming.info
        : null;

    if (call) {
      try {
        // A REAL Twilio reject. This is what lets the server's <Dial action>
        // fall through to voicemail. We never fake a local completion.
        call.reject?.();
      } catch {
        /* ignore */
      }
    }

    incomingCallRef.current = null;
    if (info) setIncoming({ kind: 'rejected', info });
    else setIncoming({ kind: 'idle' });

    electionRef.current?.broadcast({
      type: 'incoming_rejected',
      callSid: info?.providerCallSid,
    });
  }, [incoming]);

  const dismissIncoming = useCallback(() => {
    setIncoming({ kind: 'idle' });
  }, []);

  // ------------------------------------------------------------------
  // Incoming per-call recording preference (additive)
  // ------------------------------------------------------------------

  /**
   * Set the recording preference for the currently ringing Call SID.
   * Local state first (immediate UI feedback), then broadcast to the
   * same user's other surface via the server so mobile can mirror.
   * Non-blocking: a broadcast failure never blocks the toggle from
   * flipping locally; the local device's preference still wins at
   * accept time.
   */
  const setIncomingRecordingPreference = useCallback(
    (callSid: string, record: boolean) => {
      const timestamp = Date.now();
      setRecordingPrefs((prev) => {
        const existing = prev[callSid];
        // Ignore an older-timestamp toggle if a fresher one already
        // landed (defends against local double-click races).
        if (existing && existing.timestamp > timestamp) return prev;
        return {
          ...prev,
          [callSid]: { record, timestamp, sourceDevice: 'web' },
        };
      });
      void dispatch(
        telephonyApi.endpoints.postIncomingRecordingPreference.initiate({
          callSid,
          record,
          sourceDevice: 'web',
          timestamp,
        }),
      );
    },
    [dispatch],
  );

  // Subscribe to preference updates for THIS user (mobile-originated
  // toggles). `emitToUser` delivers only to sockets bound to req.user.id,
  // so cross-user leakage is impossible.
  useEffect(() => {
    if (!userId) return;
    const unsubscribe = realtimeClient.on<{
      callSid: string;
      record: boolean;
      sourceDevice: 'web' | 'mobile';
      timestamp: number;
      userId: string;
    }>(
      // Wildcard topic is safe here — emitToUser targets sockets by
      // userId, so this handler only fires for THIS user's events.
      '*',
      (payload) => {
        if (!payload || typeof payload.callSid !== 'string') return;
        // Ignore echoes from THIS device.
        if (payload.sourceDevice === 'web') return;
        setRecordingPrefs((prev) => {
          const existing = prev[payload.callSid];
          if (existing && existing.timestamp >= payload.timestamp) return prev;
          return {
            ...prev,
            [payload.callSid]: {
              record: payload.record === true,
              timestamp: payload.timestamp,
              sourceDevice: payload.sourceDevice,
            },
          };
        });
      },
      RealtimeEventType.TELEPHONY_INCOMING_CALL_RECORDING_PREF_CHANGED,
    );
    return unsubscribe;
  }, [userId]);

  // Prune preference entries when the invite terminates or the tab
  // changes user / workspace. Bounded by construction — the map only
  // ever holds entries for currently- or recently-ringing SIDs, and
  // this effect prunes anything not currently 'incoming_ringing' /
  // 'accepting'. That covers rejected / canceled / failed / idle.
  useEffect(() => {
    const activeSid =
      (incoming.kind === 'incoming_ringing' || incoming.kind === 'accepting') &&
      incoming.info.providerCallSid
        ? incoming.info.providerCallSid
        : null;
    setRecordingPrefs((prev) => {
      // Fast path: nothing to prune.
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const next: typeof prev = {};
      let changed = false;
      for (const k of keys) {
        if (k === activeSid) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [incoming.kind, incoming.kind === 'incoming_ringing' || incoming.kind === 'accepting' ? incoming.info.providerCallSid : null]);

  // Workspace/user switch or logout: hard-clear.
  useEffect(() => {
    setRecordingPrefs({});
  }, [workspaceId, userId]);

  // Unmount: never leave a Call ringing into the void.
  useEffect(() => {
    return () => {
      if (incomingCallRef.current) {
        try {
          incomingCallRef.current.reject?.();
        } catch {
          /* ignore */
        }
        incomingCallRef.current = null;
      }
    };
  }, []);

  // Convenience: the preference for the CURRENTLY-ringing SID, if any.
  const currentRingingSid =
    (incoming.kind === 'incoming_ringing' || incoming.kind === 'accepting') &&
    incoming.info.providerCallSid
      ? incoming.info.providerCallSid
      : null;
  const currentRecordingPreference =
    currentRingingSid != null && recordingPrefs[currentRingingSid]?.record === true;

  const value = useMemo<TelephonyContextValue>(
    () => ({
      voice,
      incoming,
      acceptIncoming,
      rejectIncoming,
      dismissIncoming,
      isLeader,
      incomingAllowed,
      incomingRecordingPreference: currentRecordingPreference,
      setIncomingRecordingPreference,
    }),
    [
      voice,
      incoming,
      acceptIncoming,
      rejectIncoming,
      dismissIncoming,
      isLeader,
      incomingAllowed,
      currentRecordingPreference,
      setIncomingRecordingPreference,
    ]
  );

  return <TelephonyContext.Provider value={value}>{children}</TelephonyContext.Provider>;
}

export default TelephonyProvider;
